import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { addComment } from "../../../../../../../services/apiComment";
import { useResponsive } from "../../../../../../../hooks/useResponsive";
import { useBoardUsers } from "../../../../../../../reactQuery/users";
import useMutate from "../../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../../../reactQuery/response";
import {
  getEditorCommentValue,
  getTaggedUserIds,
} from "../../hooks/commentUtils";

export function useDetailAddCommentState(cardId) {
  const [comment, setComment] = useState("");
  const [showEditor, setShowEditor] = useState(false);
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [elementPos, setElementPos] = useState();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState([]);
  const [isDisable, setIsDisable] = useState(true);
  const [selectedUserForTag, setSelectedUserForTag] = useState([]);
  const editorRef = useRef(null);
  const { id } = useBoardIdByKey();
  const [searchParams] = useSearchParams();
  const { isLoading, data } = useBoardUsers(searchParams.get("brd_id") || id);
  const users = useMemo(() => getResponseData(data, []), [data]);
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.users.logInUser);
  const isMobile = useResponsive("down", "md");

  const firstName = user?.first_name?.[0];
  const lastName = user?.last_name?.[0];
  const name = `${firstName || ""}${lastName || ""}`;
  const tooltipName = `${user?.first_name || ""} ${user?.last_name || ""}`;

  useEffect(() => {
    setSelectedUserForTag(users);
  }, [users]);

  const { mutate: addCommentmutate } = useMutate(
    addComment,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["comment", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["feeds", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["commentBoard", id],
      });
      setComment("");
      setIsDisable(true);
      setShowEditor(false);
      setSelectedId([]);
      setMentionListVisible(false);
    },
    () => {}
  );

  const handleCancel = useCallback(() => {
    setShowEditor(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!comment || isDisable) {
      return;
    }

    addCommentmutate({
      card_id: cardId,
      comment,
      tagged_user: getTaggedUserIds(selectedId, users, comment).join(",") || null,
    });
  }, [addCommentmutate, cardId, comment, isDisable, selectedId, users]);

  const handleMention = useCallback((cursorPos) => {
    const nextAnchorEl = document.getElementById("forAnchor");
    const nextRect = nextAnchorEl?.getBoundingClientRect();

    setElementPos({
      top: nextRect?.top,
      left: nextRect?.left,
    });
    setCursorPosition(cursorPos - 1);
    setAnchorEl(nextAnchorEl);
    setMentionListVisible(true);
  }, []);

  const handleUserSelect = useCallback(
    (userData) => {
      const quillEditor = editorRef?.current?.getEditor();
      setSelectedId((previousIds) => [...previousIds, userData?.user_id]);

      quillEditor.deleteText(cursorPosition, 1);
      quillEditor.insertText(cursorPosition, `@${userData?.username} `);
      quillEditor.setSelection(cursorPosition + userData?.username?.length + 1);
      setMentionListVisible(false);
    },
    [cursorPosition]
  );

  const handleComment = useCallback((value) => {
    const commentData = getEditorCommentValue(editorRef);

    if (!commentData) {
      setIsDisable(true);
      setComment("");
      return;
    }

    setComment(value);
    setIsDisable(false);
  }, []);

  const handleChange = useCallback(
    (event) => {
      const searchedText = event?.target?.value?.trim()?.toLowerCase();

      setSelectedUserForTag(
        users.filter((userData) =>
          userData?.username?.toLowerCase()?.includes(searchedText)
        )
      );
    },
    [users]
  );

  const handleMentionClose = useCallback(() => {
    const quillEditor = editorRef?.current?.getEditor();

    setMentionListVisible(false);
    quillEditor?.setSelection(cursorPosition + 1);
  }, [cursorPosition]);

  const handleEditor = useCallback(() => {
    setShowEditor(true);
  }, []);

  return {
    anchorEl,
    comment,
    editorRef,
    elementPos,
    handleCancel,
    handleChange,
    handleComment,
    handleEditor,
    handleMention,
    handleMentionClose,
    handleSave,
    handleUserSelect,
    isDisable,
    isLoading,
    isMobile,
    mentionListVisible,
    name,
    selectedUserForTag,
    setElementPos,
    showEditor,
    tooltipName,
    user,
  };
}
